import { defineStore } from "pinia";
import { AttributeOption } from "./entity";
import { AttributeOptionList } from "./list.gen";
import type {
  AttributeOptionStore,
  AttributeOptionStoreState,
} from "./store.gen.d";
import { analytics, auth } from "@/firebase";
import { type JSONData } from "../../base_classes/dto.d";
import { Pagination } from "../../base_classes/pagination";
import { Query } from "../../base_classes/query";
import { paginationAndQueriesToCacheKey } from "../../base_classes/dto_list";

/// ///////////////////////////////////////////////////////////////////////////////////////////////////
/// WARNING! THIS FILE IS AUTOMATICALLY GENERATED, ANY CHANGES WILL BE LOST ON NEXT GENERATION
/// IF YOU WANT TO EDIT THIS FILE, REMOVE THE .gen. FROM THE FILENAME. THE FILE WILL THEN BE IGNORED.
/// ///////////////////////////////////////////////////////////////////////////////////////////////////

const attributeOptionList = new AttributeOptionList();
export const useAttributeOptionStore = defineStore("attributeOptionStore", {
  state: (): AttributeOptionStoreState => ({
    activeID: null,
    cache: {},
    list: [] as AttributeOption[],
    retrievalTimestamp: 0,
    updateKey: 0,
    storeUpdateTimeout: null,
  }),
  actions: {
    initializeStore(): void {
      this.convertList();
    },
    emitFormUpdateEvent(id: string): void {
      window.dispatchEvent(
        new CustomEvent("form-update-AttributeOption", { detail: { id } }),
      );
    },
    emitStoreUpdateEvent(): void {
      // Clear existing timeout
      if (this.storeUpdateTimeout !== null) {
        clearTimeout(this.storeUpdateTimeout);
      }

      // Set new timeout
      this.storeUpdateTimeout = setTimeout(() => {
        window.dispatchEvent(new CustomEvent("store.update.AttributeOption"));
        this.storeUpdateTimeout = null;
      }, 1500);
    },
    async activate(attributeOption: AttributeOption): Promise<void> {
      // Set active
      await attributeOption
        .activate(auth.firebaseUser?.uid)
        .then(() => {
          this.activeID = attributeOption.id;
        })
        .catch((error) => {
          console.error(error);
          this.activeID = null;
        });
    },
    add(attributeOption: AttributeOption): void {
      const id = attributeOption.id;
      if (typeof id === "string" && !this.contains(id)) {
        this.list.push(attributeOption);

        // Set active
        if (this.active === null) {
          this.activeID = attributeOption.id;
        }
      } else {
        this.reinsert(attributeOption);
      }
      this.updateKey += 1;
    },
    convertList(): void {
      this.list = (this.list as AttributeOption[]).map((x: any) => {
        const attributeOption = new AttributeOption({});
        attributeOption.set(x as JSONData);
        return attributeOption;
      });
    },
    async delete(attributeOption: AttributeOption): Promise<void> {
      await attributeOption
        .delete()
        .then(() => {
          this.remove(attributeOption);
        })
        .finally(() => {
          this.emitStoreUpdateEvent();
        });
    },
    async deleteById(id: string): Promise<void> {
      const attributeOption = (this.list as AttributeOption[]).find(
        (x: AttributeOption) => x.id === id,
      );
      if (attributeOption !== undefined) {
        await this.delete(attributeOption);
      } else {
        throw new Error("Could not find AttributeOption with id: " + id);
      }
    },
    contains(id: string): boolean {
      return (this.list as AttributeOption[]).some(
        (x: AttributeOption) => x.id === id,
      );
    },
    get(id: string): AttributeOption | null {
      return (
        (this.list as AttributeOption[]).find(
          (x: AttributeOption) => x.id === id,
        ) || null
      );
    },
    reinsert(attributeOption: AttributeOption): void {
      const index = (this.list as AttributeOption[]).findIndex(
        (x: AttributeOption) => x.id === attributeOption.id,
      );
      if (index !== -1) {
        this.list = [
          ...this.list.slice(0, index),
          attributeOption,
          ...this.list.slice(index + 1),
        ];
      } else {
        this.add(attributeOption);
      }
      this.updateKey += 1;
    },
    remove({ id }: AttributeOption): void {
      const index = (this.list as AttributeOption[]).findIndex(
        (x: AttributeOption) => x.id === id,
      );
      if (index !== -1) {
        this.list = [
          ...this.list.slice(0, index),
          ...this.list.slice(index + 1),
        ];
      }
      this.updateKey += 1;
    },
    async retrieve(id: string, force = false): Promise<AttributeOption> {
      // Check if a refresh is required
      if (!force && !this.refreshRequired) {
        const attributeOption = this.get(id);
        if (attributeOption !== null) {
          return attributeOption;
        }
      }

      // Retrieve attributeOption
      const attributeOption = new AttributeOption({});

      // Get attributeOption
      await attributeOption.get([id]).then(async (entity) => {
        this.reinsert(entity);

        // Activate attributeOption
        await this.activate(entity);
      });

      return attributeOption;
    },
    async retrieveAll(force = false): Promise<{
      active: string | null;
      items: AttributeOption[];
    }> {
      // Check if a refresh is required
      if (!force && !this.refreshRequired) {
        return {
          active: this.activeID,
          items: this.list as AttributeOption[],
        };
      }

      // Clear list
      while (this.list.length > 0) {
        this.list.pop();
      }

      // Retrieve list
      await attributeOptionList.getAll().then(async (list) => {
        list.forEach((attributeOption) => {
          this.reinsert(attributeOption);
        });

        // Activate attributeOption
        if (list.length > 0) {
          if (attributeOptionList.active !== null) {
            this.activeID = attributeOptionList.active;
          } else {
            await this.activate(list[0]);
          }
        }
      });

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      // Set user property
      if (this.activeID !== null) {
        analytics.setUserProperty("attribute_option_id", this.activeID);
      }

      return {
        active: this.activeID,
        items: this.list as AttributeOption[],
      };
    },
    async retrievePaginated(
      pagination: Pagination,
      force = false,
    ): Promise<{
      pagination: Pagination;
      items: AttributeOption[];
    }> {
      // Check pagination
      let newPagination = new Pagination(pagination).deepCopy();

      // Get cachekey
      const cacheKey = pagination.toCacheKey();

      // Check if a refresh is required
      if (!force && this.cache[cacheKey]) {
        const cached = this.cache[cacheKey];
        return {
          pagination: cached.pagination.deepCopy(),
          items: cached.ids.map(
            (id: string) => this.get(id) as AttributeOption,
          ),
        };
      }

      // Retrieve list
      const items = await attributeOptionList
        .getPaginated([], pagination)
        .then(async ({ items, pagination }) => {
          items.forEach((attributeOption) => {
            this.reinsert(attributeOption);
          });

          // Set new pagination
          newPagination = pagination.deepCopy();

          return items;
        });

      // Set cache
      this.cache[cacheKey] = {
        ids: items.map((x: AttributeOption) => x.id),
        pagination: newPagination,
        retrievalTimestamp: Date.now(),
      };

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      return {
        pagination: newPagination,
        items,
      };
    },
    async query(
      queries: Query[],
      pagination: Pagination,
      force = false,
    ): Promise<{
      pagination: Pagination;
      items: AttributeOption[];
    }> {
      // Check pagination
      let newPagination = new Pagination(pagination).deepCopy();

      // Get cachekey
      const cacheKey = paginationAndQueriesToCacheKey(pagination, queries);

      // Check if a refresh is required
      if (!force && this.cache[cacheKey]) {
        const cached = this.cache[cacheKey];
        return {
          pagination: cached.pagination.deepCopy(),
          items: cached.ids.map(
            (id: string) => this.get(id) as AttributeOption,
          ),
        };
      }

      // Retrieve list
      const items = await attributeOptionList
        .query(queries, pagination)
        .then(async ({ items, pagination }) => {
          items.forEach((attributeOption) => {
            this.reinsert(attributeOption);
          });

          // Set new pagination
          newPagination = pagination.deepCopy();

          return items;
        });

      // Set cache
      this.cache[cacheKey] = {
        ids: items.map((x: AttributeOption) => x.id),
        pagination: newPagination.deepCopy(),
        retrievalTimestamp: Date.now(),
      };

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      return {
        pagination: newPagination,
        items: items,
      };
    },
    async search(
      query: string,
      pagination: Pagination,
      force = false,
    ): Promise<{
      pagination: Pagination;
      items: AttributeOption[];
    }> {
      // Check pagination
      let newPagination = new Pagination(pagination).deepCopy();

      // Get cachekey
      const cacheKey =
        query.replaceAll(" ", "_") + "-" + pagination.toCacheKey();

      // Check if a refresh is required
      if (!force && this.cache[cacheKey]) {
        const cached = this.cache[cacheKey];
        return {
          items: cached.ids.map(
            (id: string) => this.get(id) as AttributeOption,
          ),
          pagination: cached.pagination.deepCopy(),
        };
      }

      // Retrieve list
      const items = await attributeOptionList
        .search(query, pagination)
        .then(async ({ items, pagination }) => {
          items.forEach((attributeOption) => {
            this.reinsert(attributeOption);
          });

          // Set new pagination
          newPagination = pagination.deepCopy();

          return items;
        });

      // Set cache
      this.cache[cacheKey] = {
        ids: items.map((x: AttributeOption) => x.id),
        pagination: newPagination.deepCopy(),
        retrievalTimestamp: Date.now(),
      };

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      return {
        items: items,
        pagination: newPagination,
      };
    },
    async update(attributeOption: AttributeOption): Promise<string> {
      // Update attributeOption
      await attributeOption.update();

      // Reinsert attributeOption
      this.reinsert(attributeOption);

      // Activate attributeOption
      if (this.active === null) {
        await this.activate(attributeOption);
      }

      this.emitStoreUpdateEvent();

      return attributeOption.id;
    },
  },
  getters: {
    active(): AttributeOption | null {
      if (this.activeID === null) {
        return null;
      }

      const attributeOption = (this.list as AttributeOption[]).find(
        (x: AttributeOption) => x.id === this.activeID,
      );
      if (attributeOption === undefined) {
        return null;
      }

      return attributeOption;
    },
    refreshRequired(): boolean {
      return (
        this.retrievalTimestamp === 0 ||
        this.retrievalTimestamp < Date.now() - 30 * 60000 ||
        this.list.length === 0
      );
    },
  },
});

// Export a function to create a new instance of the store
export function createAttributeOptionStore(): AttributeOptionStore {
  const store = useAttributeOptionStore();
  // Run the initializeStore action when the store is created
  store.initializeStore();
  return store as unknown as AttributeOptionStore; // TODO: Fix this
}
